import { Card, Stack, Typography } from "@mui/material";
import { TextEditor, TextEditorRef } from "../../../components";
import {
  useEditAgendaItemTextContent,
  useGetAgendaItemTextContent,
  useLockAgendaItemTextContent,
  useUnlockAgendaItemTextContent,
} from "../../../http";
import { useEffect, useRef, useState } from "react";
import { useSession } from "../../../hooks";

interface IProps {
  agendaStepGuid: string;
}

const DEBOUNCE_TIMEOUT_MS = 500;

const NotepadContentAgendaStep = (props: IProps) => {
  const { userId } = useSession();
  const { data, isLoading, isFetching } = useGetAgendaItemTextContent(props.agendaStepGuid);
  const { mutate: updateText } = useEditAgendaItemTextContent();
  const { mutate: lockText } = useLockAgendaItemTextContent();
  const { mutate: unlockText } = useUnlockAgendaItemTextContent();
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const textEditor = useRef<TextEditorRef>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    // Initial load, set the content of the text editor to the data from the server
    if (!isLoading) {
      textEditor.current?.setContent(data?.text ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    // Only update the text editor content to the refetched data if the user is not the active editor
    if (!isFetching && !isEditing) {
      textEditor.current?.setContent(data?.text ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  async function handleDescriptionChanged(value: string) {
    clearTimeout(timerRef.current);

    if (value === data?.text) {
      // If the value is the same as the current text, do not update
      return;
    }

    if (data != null && (data.lockedByUser === null || data.lockedByUser.userId === userId)) {
      timerRef.current = setTimeout(() => {
        updateText({
          agendaStepGuid: props.agendaStepGuid,
          text: value,
        });
      }, DEBOUNCE_TIMEOUT_MS);
    }
  }

  function handleTextEditorBlur(value: string) {
    clearTimeout(timerRef.current);

    if (value === data?.text) {
      unlockText({ agendaStepGuid: props.agendaStepGuid }, { onSuccess: () => setIsEditing(false) });
      return;
    }

    if (data != null && (data.lockedByUser === null || data.lockedByUser.userId === userId)) {
      updateText(
        {
          agendaStepGuid: props.agendaStepGuid,
          text: value,
        },
        {
          onSettled: () => {
            unlockText({ agendaStepGuid: props.agendaStepGuid }, { onSuccess: () => setIsEditing(false) });
          },
        }
      );
    }
  }

  function handleTextEditorFocus() {
    setIsEditing(true);
    lockText({ agendaStepGuid: props.agendaStepGuid }, { onError: () => setIsEditing(false) });
  }

  return (
    <Stack spacing={0.5}>
      <Card>
        <TextEditor
          ref={textEditor}
          initialValue={data?.text ?? ""}
          readonly={isLoading || (data?.lockedByUser !== null && data?.lockedByUser.userId !== userId)}
          onChange={(value) => {
            handleDescriptionChanged(value);
          }}
          onBlur={handleTextEditorBlur}
          onFocus={handleTextEditorFocus}
        />
      </Card>

      {data?.lockedByUser && data.lockedByUser.userId !== userId && (
        <Typography variant="caption" sx={{ pl: 0.5 }}>
          {`${data.lockedByUser.displayName} is editing...`}
        </Typography>
      )}
    </Stack>
  );
};

export { NotepadContentAgendaStep };
